import { Box, Typography, useTheme, Card, CardContent, TextField, Button, Grid, MenuItem, Alert, Chip, IconButton, Collapse, Divider, Stack, Select, Skeleton, InputLabel, FormControl, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment, Tooltip, CircularProgress, FormHelperText, Slider, Popover, Drawer, ListItemText, Tabs, Tab } from "@mui/material";
import { tokens } from "../../theme";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mockDatasetDefinitions } from "../../data/mockDatasetDefinitions";
import { loadTrainingDefaults } from "../../config/trainingDefaults";

// Normalize a model name (display or canonical) into a fuzzy key for matching.
// Reconciles spelling variants between mockDatasetDefinitions.compatible_models
// (display names: "UNet", "ConvNeXt_3D") and MODEL_OPTIONS_BY_PIPELINE values
// (canonical ids: "unet", "convnext_3d"). Lowercase + strip non-alphanumeric so
// "UNet"/"U-Net"/"unet" all map to "unet" and "ConvNeXt_3D"/"convnext_3d" both
// map to "convnext3d".
const normalizeModelName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

import { TabularIcon, ImagingClassificationIcon, ImagingSegmentationIcon } from '../../components/TaskTypeIcons';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import TagIcon from '@mui/icons-material/Tag';
import AbcIcon from '@mui/icons-material/Abc';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HeaderWithControls from "../../components/HeaderWithControls";
import HelpModal from "../../components/HelpModal";
import DatasetSelectionModal from "../../components/modals/DatasetSelectionModal";
import SiteSelectionPanel from "./components/SiteSelectionPanel";
import SimulationModeAccordion from "./components/SimulationModeAccordion";
import SchemaPreCheckPanel from "./components/SchemaPreCheckPanel";
import FeatureDistributionPanel from "./components/FeatureDistributionPanel";
import StepHeader from "../../components/StepHeader";
import { resolveDatasetDisplayNames } from "../../utils/datasetDisplay";

// Module-level cache to persist across component mounts
let globalLastDatasetPath = null;

const Configs = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { 
    config, 
    configLoading, 
    configSaving, 
    availableModels, 
    availableDatasets, 
    datasets, // Add this
    displayableFeatures,
    displayableFeaturesLoading,
    selectedFeatures,
    setSelectedFeatures,
    saveConfig,
    trainingDefaults,
    updateSelectedFeatures,
    fetchSelectedFeatures,
    fetchDisplayableFeatures,
    clearAllTrainingData,
    featureMetadata,
    perDatasetFeaturesMap,
    setPerDatasetFeaturesMap,
    errors,
    // Phase F1: site-based selection (mock-backed)
    selectedSites,
    setSelectedSites,
    siteCapabilities,
  } = useAppContext();

  const [processingType, setProcessingType] = useState("tabular");

  // Hyperparameter starting values come from the org's saved Settings defaults
  // (Settings → Federated Training), falling back to the platform defaults.
  const [configs, setConfigs] = useState(() => {
    const td = loadTrainingDefaults();
    return {
    processing_type: "tabular",
    "num-server-rounds": td["num-server-rounds"],
    "fraction-evaluate": 0.5,
    "local-epochs": td["local-epochs"],
    "learning-rate": td["learning-rate"],
    "batch-size": td["batch-size"],
    model_type: td.model_type,
    dataset_path: "", // Legacy support or primary
    selected_datasets: [], // New array support
    partitioning_method: "iid",
    l1_lambda: 0.001,
    l2_lambda: 0.001,
    regularization_norm: 2,
    segmentation_config: {
      in_channels: 1,
      n_classes: 1,
      partitioning_method: "iid"
    },
    selected_features: [],
    feature_filters: {}
    };
  });

  // "Use This Configuration" (restoring a *past run*) arrives with
  // location.state.restored — in that mode the restored run's hyperparameters
  // win. Every other entry to this page is treated as a fresh configuration
  // where the user's saved Settings defaults are the master template.
  const restoringRun = Boolean(location.state?.restored);

  // Becomes true once the user edits any hyperparameter on this page, so the
  // Settings-defaults sync below stops clobbering an in-progress edit.
  const hyperParamsTouchedRef = useRef(false);

  // The five hyperparameter fields driven by Settings → Federated Training.
  const applySettingsHyperparams = (prev, td) => {
    const next = {
      ...prev,
      model_type: td.model_type,
      "num-server-rounds": td["num-server-rounds"],
      "local-epochs": td["local-epochs"],
      "batch-size": td["batch-size"],
      "learning-rate": td["learning-rate"],
    };
    const unchanged =
      prev.model_type === next.model_type &&
      prev["num-server-rounds"] === next["num-server-rounds"] &&
      prev["local-epochs"] === next["local-epochs"] &&
      prev["batch-size"] === next["batch-size"] &&
      prev["learning-rate"] === next["learning-rate"];
    return unchanged ? prev : next; // avoid a pointless re-render
  };

  // Master-default behaviour: pre-fill the hyperparameters from the user's saved
  // Settings whenever this is a fresh configuration (not a past-run restore) and
  // the user hasn't already edited them in this session. Runs even though a
  // backend `config` is always present, because that config's hyperparameters
  // are just the server defaults — Settings is the source of truth here.
  useEffect(() => {
    if (!trainingDefaults) return;
    if (restoringRun) return;            // restored run keeps its own values
    if (hyperParamsTouchedRef.current) return; // don't clobber an active edit
    setConfigs((prev) => applySettingsHyperparams(prev, trainingDefaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingDefaults, restoringRun]);

  // Handle incoming dataset selection from Datasets scene
  useEffect(() => {
    if (location.state && (location.state.dataset_path || location.state.selected_datasets)) {
      console.log("[DEBUG_NAV] Setting dataset from navigation state:", location.state);
      const newSelected = location.state.selected_datasets || [location.state.dataset_path];
      console.log("[DEBUG_NAV] newSelected array:", newSelected);
      const primary = location.state.dataset_path || newSelected[0];

      setConfigs(prev => ({
        ...prev,
        dataset_path: primary,
        selected_datasets: newSelected, // Initialize array from nav state
        selected_features: [] // Reset features when switching datasets
      }));
    } else {
      // Default initialization if no state passed
      let fallbackDatasets = [];
      try {
          const savedStr = localStorage.getItem('savedConfig');
          if (savedStr) {
              const saved = JSON.parse(savedStr);
              if (saved?.dataset_config?.dataset_path === configs.dataset_path) {
                  fallbackDatasets = saved?.dataset_config?.selected_datasets || [];
              }
          }
      } catch(e) {}

      if (configs.dataset_path && configs.selected_datasets.length === 0) {
           setConfigs(prev => ({ ...prev, selected_datasets: fallbackDatasets.length > 0 ? fallbackDatasets : [prev.dataset_path] }));
      }
    }
  }, [location.state]); // Removed availableDatasets to isolate navigation interaction

  const [drawerOpen, setDrawerOpen] = useState(false);

  // F1.5 v2: Advanced · Simulation Mode accordion state. Default collapsed.
  // Auto-expanded once on mount when the user navigates in from /datasets
  // carrying a `dataset_path` in router state — preserves the legacy demo flow.
  const [simulationExpanded, setSimulationExpanded] = useState(
    () => Boolean(location.state?.dataset_path)
  );
  // F1.5 v2: inline blocked-save guard message shown above the Save button.
  const [saveBlockedMessage, setSaveBlockedMessage] = useState(null);
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [currentInspectingFeature, setCurrentInspectingFeature] = useState(null);

  const [helpOpen, setHelpOpen] = useState(false);
  const featureUpdateTimeoutRef = useRef(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [featureSearch, setFeatureSearch] = useState("");
  const [previewFilterMode, setPreviewFilterMode] = useState('all'); // 'all', 'filtered', 'unfiltered'
  const [currentFeatureTab, setCurrentFeatureTab] = useState("intersection");

  // Session Preview "Datasets" line — show the logical dataset name(s) that
  // will actually power the run. Same resolver as /execute Configuration
  // Details so both surfaces stay in sync.
  const sessionPreviewDatasetNames = useMemo(() => {
    const { names } = resolveDatasetDisplayNames({
      sites: selectedSites,
      selectedDatasets: configs.selected_datasets,
      siteCapabilities,
      availableDatasets,
    });
    return names;
  }, [selectedSites, siteCapabilities, configs.selected_datasets, availableDatasets]);

  // --- Helpers ---
  const inferFeatureType = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('age') || lower.includes('year') || lower.includes('count') || lower.includes('days') || lower.includes('id')) return 'INT';
    if (lower.includes('percent') || lower.includes('ratio') || lower.includes('rate') || lower.includes('mean') || lower.includes('std')) return 'FLOAT';
    if (lower.includes('is_') || lower.includes('has_') || lower.includes('grade') || lower.includes('stage') || lower.includes('status')) return 'CAT';
    return 'FLOAT'; // Default fallback
  };

  // --- Effects (Logic Preserved) ---
  useEffect(() => {
    if (config) {
      console.log("[DEBUG_CTX] Received config from context:", config);
      const mergedConfig = {
        ...configs,
        ...config.app_config,
        ...config.dataset_config,
        ...(config.imaging_config && config.imaging_config),
        // Restore the full segmentation_config sub-object (in_channels,
        // n_classes, partitioning_method) so segmentation runs reload with
        // their saved values instead of the form defaults.
        ...(config.segmentation_config && { segmentation_config: { ...configs.segmentation_config, ...config.segmentation_config } }),
        feature_filters: config.tabular_config?.feature_filters || config.dataset_config?.feature_filters || {}
      };

      // Settings-as-master-default: unless we're restoring a specific past run
      // (or the user already edited a hyperparameter this session), the five
      // hyperparameter fields come from the user's saved Settings defaults — NOT
      // from config.app_config, whose values are just the backend's server-side
      // defaults. This is what makes a Settings change visibly pre-fill here.
      if (!restoringRun && !hyperParamsTouchedRef.current) {
        const td = trainingDefaults || loadTrainingDefaults();
        mergedConfig.model_type = td.model_type;
        mergedConfig["num-server-rounds"] = td["num-server-rounds"];
        mergedConfig["local-epochs"] = td["local-epochs"];
        mergedConfig["batch-size"] = td["batch-size"];
        mergedConfig["learning-rate"] = td["learning-rate"];
      }

      // CRITICAL: Preserve navigation state if present
      // CRITICAL: Preserve navigation state if present
      if (location.state && (location.state.dataset_path || location.state.selected_datasets)) {
          console.log("[DEBUG_CTX] Overriding context config with navigation state:", location.state);
          // Prioritize array of datasets if present
          if (location.state.selected_datasets && location.state.selected_datasets.length > 0) {
              console.log("[DEBUG_CTX] Using selected_datasets from nav:", location.state.selected_datasets);
              mergedConfig.selected_datasets = location.state.selected_datasets;
              mergedConfig.dataset_path = location.state.selected_datasets[0]; 
          } else if (location.state.dataset_path) {
              mergedConfig.dataset_path = location.state.dataset_path;
              // Only overwrite selected_datasets if it's empty or we're strictly single-select navigating
              if (!mergedConfig.selected_datasets || mergedConfig.selected_datasets.length <= 1) {
                  mergedConfig.selected_datasets = [location.state.dataset_path];
              }
          }

      } else {
          // Ensure selected_datasets is synced if coming from context without nav state
          let fallbackDatasets = [];
          try {
              const savedStr = localStorage.getItem('savedConfig');
              if (savedStr) {
                  const saved = JSON.parse(savedStr);
                  if (saved?.dataset_config?.dataset_path === mergedConfig.dataset_path) {
                      fallbackDatasets = saved?.dataset_config?.selected_datasets || [];
                  }
              }
          } catch(e) {}

          if (mergedConfig.dataset_path && (!mergedConfig.selected_datasets || mergedConfig.selected_datasets.length === 0)) {
              mergedConfig.selected_datasets = fallbackDatasets.length > 0 ? fallbackDatasets : [mergedConfig.dataset_path];
          }
      }
      
      console.log("[DEBUG_CTX] Final merged selected_datasets:", mergedConfig.selected_datasets);
      
      // Normalize legacy/mismatched model names
      if (mergedConfig.model_type) {
          mergedConfig.model_type = String(mergedConfig.model_type).toLowerCase();
      }
      
      if (mergedConfig.model_type === 'ft_transformer') {
          mergedConfig.model_type = 'fttransformer';
      }

      // Validate model_type against tabular options ONLY when the saved
      // config was tabular. Imaging tasks have their own model spaces.
      const validTabularModels = ["logreg", "mlp", "tabnet", "fttransformer"];
      const incomingType = mergedConfig.processing_type || "tabular";
      if (incomingType === "tabular" && !validTabularModels.includes(mergedConfig.model_type)) {
          console.warn(`[Configs] Unknown tabular model '${mergedConfig.model_type}' detected. Defaulting to 'logreg'.`);
          mergedConfig.model_type = "logreg";
      }

      // Imaging Classification / Segmentation are unlocked in the prototype;
      // honour whatever processing_type the saved config carried, falling back
      // to tabular only when none is set.
      const validProcessingTypes = ["tabular", "imaging_classification", "imaging_segmentation"];
      const resolvedType = validProcessingTypes.includes(incomingType) ? incomingType : "tabular";
      mergedConfig.processing_type = resolvedType;
      setConfigs(mergedConfig);
      setProcessingType(resolvedType);

      // Restore the site/capability picker from the loaded config so the
      // F1.5 site-local selections re-appear when "Use This Configuration"
      // brings a past run back into the form. Without this, sites[] were
      // stored and passed through but the picker stayed empty, blocking save.
      if (Array.isArray(config.sites) && typeof setSelectedSites === 'function') {
        const restorableSites = config.sites.filter(
          (s) => s && s.site_id
        );
        if (restorableSites.length > 0) {
          setSelectedSites(restorableSites);
        }
      }

      // Restore selected tabular features. selectedFeatures is owned by
      // AppContext (not local form state), so saveConfig alone doesn't bring
      // it back — repopulate it from the loaded tabular_config.
      const restoredFeatures = config.tabular_config?.selected_features;
      if (Array.isArray(restoredFeatures) && restoredFeatures.length > 0 && typeof setSelectedFeatures === 'function') {
        setSelectedFeatures(restoredFeatures);
        setHasInitialized(true); // prevent the default ["age"] effect from clobbering
      }
    }
  }, [config, location.state]); // Add location.state to dependencies to ensure we have latest match

  useEffect(() => {
    if (displayableFeatures.length > 0 && processingType === "tabular" && selectedFeatures.length === 0 && !hasInitialized) {
      const defaultFeature = ["age"];
      updateSelectedFeatures(defaultFeature);
      setConfigs(prev => ({ ...prev, selected_features: defaultFeature }));
      setHasInitialized(true);
    }
  }, [displayableFeatures, processingType, selectedFeatures.length, updateSelectedFeatures, hasInitialized]);

  useEffect(() => {
    if (datasets.length > 0) {
      const filteredDatasets = datasets.filter((datasetPath) => {
        const isLIDCDataset = datasetPath.includes('LIDC_cls_patches');
        const isSegmentationDataset = datasetPath.includes('LIDC_IDRI_segmentation') || datasetPath.includes('segmentation');
        
        if (processingType === "imaging_classification") return isLIDCDataset;
        else if (processingType === "imaging_segmentation") return isSegmentationDataset;
        else return !isLIDCDataset && !isSegmentationDataset;
      });

      // Try to recover from localstorage just in case this effect runs before the other one completes its state update
      let currentDatasets = configs.selected_datasets || [];
      if (currentDatasets.length === 0) {
           try {
              const savedStr = localStorage.getItem('savedConfig');
              if (savedStr) {
                  const saved = JSON.parse(savedStr);
                  if (saved?.dataset_config?.selected_datasets?.length > 0) {
                      currentDatasets = saved.dataset_config.selected_datasets;
                  }
              }
          } catch(e) {}
      }

      const currentDatasetsValid = currentDatasets.every(ds => filteredDatasets.includes(ds)) && currentDatasets.length > 0;

      // CRITICAL FIX: Check if we have incoming navigation state that should be respected
      // If we just navigated here with a dataset, don't overwrite it with default
      const hasNavState = location.state && (location.state.dataset_path || location.state.selected_datasets);
      
      if (!hasNavState && !currentDatasetsValid) {
        let defaultDataset;
        if (processingType === "imaging_classification") {
          defaultDataset = filteredDatasets.find(d => d.includes('LIDC_cls_patches'));
        } else if (processingType === "imaging_segmentation") {
          defaultDataset = filteredDatasets.find(d => d.includes('LIDC_IDRI_segmentation'));
        } else {
          defaultDataset = filteredDatasets.find(d => d.includes('dataset_primary_site.csv')) || filteredDatasets[0];
        }
        
        if (defaultDataset) {
          setConfigs(prev => ({ 
              ...prev, 
              dataset_path: defaultDataset,
              selected_datasets: [defaultDataset]
          }));
        }
      }
    }
  }, [datasets, processingType, configs.dataset_path, location.state]);

  // --- Handlers ---
  const handleChange = (field, value) => {
    let processedValue = value;

    // Once the user touches any hyperparameter, stop auto-syncing them from
    // Settings defaults so we don't overwrite their in-session edit.
    if (["model_type", "num-server-rounds", "local-epochs", "batch-size", "learning-rate"].includes(field)) {
      hyperParamsTouchedRef.current = true;
    }

    if (field.startsWith("segmentation_config.")) {
      const nestedField = field.split(".")[1];
      setConfigs(prev => ({
        ...prev,
        segmentation_config: { ...prev.segmentation_config, [nestedField]: value }
      }));
      return;
    }
    
    if (["num-server-rounds", "local-epochs", "batch-size"].includes(field)) {
      if (value !== "") {
        const val = parseInt(value);
        if (!isNaN(val)) processedValue = Math.max(1, val);
      }
    }
    
    if (["learning-rate", "fraction-evaluate"].includes(field)) {
       if (value !== "") {
        const val = parseFloat(value);
        if (!isNaN(val)) {
            processedValue = val;
            if (field === "learning-rate" && (val <= 0 || val > 1)) processedValue = 0.01;
            if (field === "fraction-evaluate" && (val <= 0 || val > 1)) processedValue = 0.5;
        }
       }
    }
    
    if (field === "model_type") processedValue = String(value);

    if (field === "dataset_path") {
        // Legacy single-select handler - redirect to array logic
        const newPath = String(value);
        const newSelection = [newPath];
        
        setSelectedFeatures([]);
        setConfigs(prev => ({ 
            ...prev, 
            dataset_path: newPath, 
            selected_datasets: newSelection,
            selected_features: [] 
        }));
        return;
    }
    
    setConfigs(prev => ({ ...prev, [field]: processedValue }));
  };

  const debouncedUpdateFeatures = useCallback((features) => {
    if (featureUpdateTimeoutRef.current) clearTimeout(featureUpdateTimeoutRef.current);
    featureUpdateTimeoutRef.current = setTimeout(() => {
      updateSelectedFeatures(features);
    }, 300);
  }, [updateSelectedFeatures]);

  const handleFeatureSelectionChange = (feature, checked) => {
    let newSelectedFeatures = checked 
      ? [...selectedFeatures, feature] 
      : selectedFeatures.filter(f => f !== feature);
      
    setSelectedFeatures(newSelectedFeatures);
    setConfigs(prev => ({ ...prev, selected_features: newSelectedFeatures }));
    debouncedUpdateFeatures(newSelectedFeatures);
    debouncedUpdateFeatures(newSelectedFeatures);
  };

  const handleFilterChange = (feature, type, value) => {
    setConfigs(prev => {
        const currentFilters = prev.feature_filters || {};
        const featFilter = currentFilters[feature] || {};
        
        let newFeatFilter;
        if (type === 'min' || type === 'max') {
            newFeatFilter = { ...featFilter, [type]: value };
        } else if (type === 'values') {
            newFeatFilter = { ...featFilter, values: value };
        }

        return {
            ...prev,
            feature_filters: {
                ...currentFilters,
                [feature]: newFeatFilter
            }
        };
    });
  };

  // --- Multi-Dataset Selection Handler ---
  const handleDatasetsConfirmed = (newSelectedPaths) => {
      setDatasetModalOpen(false);
      if (newSelectedPaths.length === 0) return;

      const primaryPath = newSelectedPaths[0]; // Keep one as "primary" for legacy `dataset_path` compatibility
      
      setConfigs(prev => ({
          ...prev,
          dataset_path: primaryPath,
          selected_datasets: newSelectedPaths,
          selected_features: [] // Reset features logic will re-run
      }));
      
      setSelectedFeatures([]);
      
      // Trigger Logic to Fetch & Intersect Features
      // We will let the useEffect handle this via `configs.selected_datasets` change
  };
  
  // Override the previous useEffect for fetching to actually set `intersectedFeatures`
  const [intersectedFeatures, setIntersectedFeatures] = useState([]);
  const [isIntersectingFeatures, setIsIntersectingFeatures] = useState(false);
  const lastFetchedKeyRef = useRef(null);

  useEffect(() => {
    if (configs.selected_datasets.length > 0 && processingType === "tabular") {
      const currentDatasetsKey = [...configs.selected_datasets].sort().join(',');
      
      // Re-calculate if datasets actually changed for this component mount
      if (lastFetchedKeyRef.current !== currentDatasetsKey) {
        lastFetchedKeyRef.current = currentDatasetsKey;
        globalLastDatasetPath = currentDatasetsKey; // Maintain cross-component sync just in case

        const performIntersection = async () => {
             setIsIntersectingFeatures(true);
             try {
                // Fetch sequentially because the backend `get_feature_names_and_plot` relies on global variables
                // that create a race condition if fetched concurrently in threads.
                const results = [];
                for (const path of configs.selected_datasets) {
                    const res = await fetchDisplayableFeatures(path);
                    results.push(res);
                }
                if (results.length > 0) {
                    // Union (All Features)
                    const allFeaturesSet = new Set();
                    results.forEach(res => res.forEach(f => allFeaturesSet.add(f)));
                    const allFeatures = Array.from(allFeaturesSet);
                    setIntersectedFeatures(allFeatures);
                    
                    // Create map for individual dataset views
                    const fMap = {};
                    configs.selected_datasets.forEach((path, i) => {
                        fMap[path] = results[i] || [];
                    });
                    setPerDatasetFeaturesMap(fMap);
                    // Also update context with the common ones so other components (like `filteredFeatures` logic) work downstream
                    // We can't "set" context directly easily, but `fetchDisplayableFeatures` sets it to the *last* one fetched.
                    // This is inconsistent. 
                    // ideally we should have `updateDisplayableFeatures` in context.
                    // For now, `intersectedFeatures` local state will be used for rendering the list.
                }
             } catch (e) {
                 console.error("Failed to intersect features", e);
             } finally {
                 setIsIntersectingFeatures(false);
             }
        };
        performIntersection();
      }
    }
  }, [configs.selected_datasets, processingType, fetchDisplayableFeatures, setPerDatasetFeaturesMap]);


  const handleRemoveFilter = (feature, e) => {
    e.stopPropagation();
    setConfigs(prev => {
        const currentFilters = { ...prev.feature_filters };
        delete currentFilters[feature];
        return {
            ...prev,
            feature_filters: currentFilters
        };
    });
  };



  const handleOpenDrawer = (feature, e) => {
    e.stopPropagation();
    setCurrentInspectingFeature(feature);
    setDrawerOpen(true);
    
    // Auto-select if not selected?
    if (!selectedFeatures.includes(feature)) {
        handleFeatureSelectionChange(feature, true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setCurrentInspectingFeature(null);
  };

  const handleSelectAllFeatures = (selectAll) => {
    const newSelectedFeatures = selectAll ? [...displayableFeatures] : [];
    setSelectedFeatures(newSelectedFeatures);
    setConfigs(prev => ({ ...prev, selected_features: newSelectedFeatures }));
    debouncedUpdateFeatures(newSelectedFeatures);
  };

  const handleProcessingTypeChange = (newType) => {
    // Prototype: three task types are clickable in the Processing Module row.
    // Each maps to a distinct site-capability filter for the Federation Sites
    // panel below and may use a different model space (only tabular has its
    // selector wired today; imaging model selectors arrive in a later phase).
    const allowed = ["tabular", "imaging_classification", "imaging_segmentation"];
    if (!allowed.includes(newType)) return;
    if (newType === processingType) return; // no-op when re-clicking active card

    setProcessingType(newType);

    // Default model = first option in the new pipeline's list. The MODEL
    // ARCHITECTURE select narrows to this list, so resetting keeps the
    // currently-chosen model_type consistent with the visible options.
    const defaultModel =
      (MODEL_OPTIONS_BY_PIPELINE[newType] && MODEL_OPTIONS_BY_PIPELINE[newType][0]?.value) ||
      "";

    if (newType === "tabular") {
      if (displayableFeatures.length > 0) {
        setSelectedFeatures(displayableFeatures.slice(0, 5));
      }
      setConfigs((prev) => ({
        ...prev,
        processing_type: "tabular",
        model_type: defaultModel || "logreg",
        "learning-rate": 0.01,
        "batch-size": 32,
        selected_features: displayableFeatures.slice(0, 5),
      }));
    } else {
      // Imaging task. Clear tabular feature selection (schema-incompatible)
      // and set model_type to the first imaging model for that task.
      setSelectedFeatures([]);
      setConfigs((prev) => ({
        ...prev,
        processing_type: newType,
        model_type: defaultModel,
        selected_features: [],
      }));
    }
  };

  const handleSave = async () => {
    // Phase F1.5 v2 — Site-local primary, Simulation fallback.
    //   - completedSites.length > 0       → payload includes `sites[]`,
    //                                        omits dataset_path & selected_datasets.
    //   - simulationExpanded + legacy ds  → byte-identical pre-F1.5 tabular payload.
    //   - neither                         → block save with inline warning.
    setSaveBlockedMessage(null);
    try {
      const completedSites = (selectedSites || []).filter(
        (s) => s && s.site_id && s.capability_id
      );
      const hasLegacySelection =
        simulationExpanded && (configs.selected_datasets?.length || 0) > 0;

      if (completedSites.length === 0 && !hasLegacySelection) {
        setSaveBlockedMessage(
          "Add a site and capability above before running training."
        );
        return;
      }

      const baseConfig = {
        app_config: {
          "num-server-rounds": configs["num-server-rounds"],
          "fraction-evaluate": configs["fraction-evaluate"],
          "local-epochs": configs["local-epochs"],
          "learning-rate": configs["learning-rate"],
          "batch-size": configs["batch-size"]
        },
        ...(configs.processing_type === "tabular" && {
          tabular_config: {
            selected_features: selectedFeatures,
            feature_filters: configs.feature_filters
          }
        }),
        // Persist imaging-classification hyperparameters so they round-trip
        // through "Use This Configuration". Previously these lived only in
        // local form state and were never stored with the run, so reloading
        // a past imaging run silently fell back to defaults.
        ...(configs.processing_type === "imaging_classification" && {
          imaging_config: {
            partitioning_method: configs.partitioning_method,
            l1_lambda: configs.l1_lambda,
            l2_lambda: configs.l2_lambda,
            regularization_norm: configs.regularization_norm,
          }
        }),
        // Persist segmentation hyperparameters for the same reason.
        ...(configs.processing_type === "imaging_segmentation" && {
          segmentation_config: {
            ...configs.segmentation_config,
          }
        }),
      };

      let configData;
      // Defensive double-wire for ProstateX (cls + seg). Backend's
      // ProstateX-specific factories (Models_imaging_classification_3d +
      // task_segmentation) historically read `prostatex_model_name` /
      // `prostatex_seg_model_name` from run_config rather than the generic
      // `model_type` channel. If Flask doesn't propagate model_type → these
      // ProstateX-specific keys, backend silently falls back to its own
      // default (SimpleCNN for cls, UNet for seg) and ignores the user's
      // selection — surfaced as a real Tier1 result with the "wrong" model.
      // Setting both keys here is a no-op if Flask doesn't pick it up, and a
      // proper fix if Flask reads them. Backend fix (single canonical
      // channel) is tracked separately in a Frontend_To_Backend handoff.
      // Use the multi-source resolver — F1.5 Sites mode doesn't populate
      // configs.dataset_path, so we have to also check selectedSites[].
      const dsRef = currentDatasetRef || '';
      const isProstatexCls = dsRef === 'prostatex_cls_v1';
      const isProstatexSeg = dsRef === 'prostatex_seg_v1';
      const prostatexExtras = {
        ...(isProstatexCls ? { prostatex_model_name: configs.model_type } : {}),
        ...(isProstatexSeg ? { prostatex_seg_model_name: configs.model_type } : {}),
      };

      if (completedSites.length > 0) {
        // Site-local primary path. Omit legacy keys entirely.
        configData = {
          ...baseConfig,
          dataset_config: {
            model_type: configs.model_type,
            processing_type: configs.processing_type,
            ...prostatexExtras,
          },
          sites: completedSites,
        };
      } else {
        // Simulation fallback. Byte-identical to pre-F1.5 tabular payload.
        configData = {
          ...baseConfig,
          dataset_config: {
            model_type: configs.model_type,
            dataset_path: configs.dataset_path || '',
            selected_datasets: configs.selected_datasets,
            processing_type: configs.processing_type,
            ...prostatexExtras,
          },
        };
      }

      // dev-0.7.9 case-sensitivity safety net — ClientApp's
      // task_tabular.get_model() dispatcher is case-sensitive (per
      // 2026-06-04 sync §②). Uppercase like "MLP" reaches the dispatcher
      // and triggers `ValueError: Unknown model_type: MLP`, failing the
      // task at round 0. Option values in MODEL_OPTIONS_BY_PIPELINE
      // above are already lowercase, but defense-in-depth in case any
      // path injects uppercase (legacy localStorage, hand-edited config,
      // etc.). Backend will ship a .lower() normalization later; this
      // one-liner is harmless once that lands.
      if (configData?.dataset_config?.model_type) {
        configData.dataset_config.model_type = String(
          configData.dataset_config.model_type
        ).toLowerCase();
      }

      // Debug aid — log the exact app_config + dataset_config we're
      // about to persist. Helps verify in DevTools console that the
      // user's form-state values actually reach POST /api/config.
      console.info('[handleSave] POST /api/config payload app_config:', configData.app_config);
      console.info('[handleSave] POST /api/config payload dataset_config:', configData.dataset_config);
      await saveConfig(configData);
      await clearAllTrainingData();

      navigate('/execute', {
        state: { animate: true },
      });

    } catch (error) {
      console.error("Navigation Error:", error);
    }
  };

  // --- Constants for UI ---
  // Pipeline → available model architectures. Mirrors the canonical model
  // identifiers used on /models. The MODEL ARCHITECTURE selector below
  // narrows to the list for the currently selected Processing Module
  // (Tabular / Imaging · Classification / Imaging · Segmentation).
  const MODEL_OPTIONS_BY_PIPELINE = {
    tabular: [
      { value: "logreg", label: "Logistic Regression" },
      { value: "mlp", label: "Multi-Layer Perceptron" },
      { value: "tabnet", label: "TabNet" },
      { value: "fttransformer", label: "FT-Transformer" },
    ],
    // Full union of imaging classification models — both LIDC (2D) and
    // ProstateX (3D). Per-dataset filtering (below) narrows this to only
    // the models the selected dataset's registry actually supports, so
    // users only see options that will pass backend's MODEL_INCOMPATIBLE
    // gate. Source of truth for valid {value, label} pairs.
    imaging_classification: [
      // LIDC (2D)
      { value: "convnext", label: "ConvNeXt" },
      { value: "resnet18", label: "ResNet18" },
      { value: "swintransformer", label: "Swin Transformer" },
      // ProstateX (3D)
      { value: "simplecnn", label: "SimpleCNN" },
      { value: "resnet18_3d", label: "ResNet18_3D" },
      { value: "multimodalfusionnet", label: "MultiModalFusionNet" },
      { value: "convnext_3d", label: "ConvNeXt_3D" },
      { value: "swintiny_3d", label: "SwinTiny_3D" },
    ],
    imaging_segmentation: [
      { value: "unet", label: "U-Net" },
      { value: "unetpp", label: "U-Net++" },
      { value: "fpn", label: "FPN" },
    ],
  };
  // Resolve the currently-selected dataset_ref from any of the 3 places FE
  // tracks it. dev-0.7.X+ uses Sites mode (F1.5 site-local primary) by default,
  // and that path does NOT populate configs.dataset_path — it stores selection
  // as selectedSites[].capability_id and we have to reverse-lookup via
  // siteCapabilities[site_id]. Falls back to the legacy single-dataset path
  // (configs.dataset_path, set when navigating from DataHub) and to the
  // multi-dataset legacy array (configs.selected_datasets[0]).
  const currentDatasetRef = useMemo(() => {
    // Each source is only accepted if its candidate value is a real
    // dataset_ref (i.e., a key in mockDatasetDefinitions). This guards
    // against a pre-existing FE quirk where configs.dataset_path /
    // selected_datasets[0] sometimes contain a stale capability_id (e.g.
    // "cap_tgh_tabular_primary") instead of a true dataset_ref (e.g.
    // "prostatex_cls_v1") — leftover from a previous selection round.
    // Validating against mockDatasetDefinitions lets us fall through to
    // the F1.5 Sites lookup (Source 3) which IS the authoritative live
    // selection, and produces the right filtered dropdown.

    // Source 1: legacy single-dataset path — only if it's a real
    // dataset_ref key in mockDatasetDefinitions.
    if (configs?.dataset_path && mockDatasetDefinitions[configs.dataset_path]) {
      return configs.dataset_path;
    }
    // Source 2: multi-dataset legacy array, first entry — same validation.
    const sel0 = configs?.selected_datasets?.[0];
    if (sel0 && mockDatasetDefinitions[sel0]) {
      return sel0;
    }
    // Source 3: F1.5 Sites mode — resolve via first selected site's
    // capability. This is authoritative for the live selection in
    // dev-0.7.X+ flows.
    const firstSel = selectedSites?.[0];
    if (firstSel?.site_id && firstSel?.capability_id) {
      const caps = siteCapabilities?.[firstSel.site_id] || [];
      const match = caps.find((c) => c?.capability_id === firstSel.capability_id);
      if (match?.dataset_ref && mockDatasetDefinitions[match.dataset_ref]) {
        return match.dataset_ref;
      }
    }
    return null;
  }, [
    configs?.dataset_path,
    configs?.selected_datasets,
    selectedSites,
    siteCapabilities,
  ]);

  // Per-dataset model picker: narrows the pipeline baseline to only the
  // models the *selected dataset's* registry entry whitelists. Pre-existing
  // behavior (when no dataset is selected, or the dataset is unknown) is
  // preserved by falling back to the full baseline. Defensive: if the
  // dataset's compatible_models intersect with the baseline is empty (which
  // would mean a registry/baseline mismatch — should never happen in
  // practice), we still show the full baseline rather than an empty dropdown.
  const currentModelOptions = useMemo(() => {
    const baseline =
      MODEL_OPTIONS_BY_PIPELINE[processingType] ||
      MODEL_OPTIONS_BY_PIPELINE.tabular;
    const datasetDef = currentDatasetRef ? mockDatasetDefinitions[currentDatasetRef] : null;
    if (!datasetDef?.compatible_models?.length) return baseline;
    const allowed = new Set(datasetDef.compatible_models.map(normalizeModelName));
    const filtered = baseline.filter(
      (opt) =>
        allowed.has(normalizeModelName(opt.value)) ||
        allowed.has(normalizeModelName(opt.label))
    );
    return filtered.length > 0 ? filtered : baseline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingType, currentDatasetRef]);

  // Auto-correct configs.model_type whenever the visible option set changes
  // (dataset switch, pipeline switch). If the currently-stored model_type is
  // no longer in the dropdown, fall to the dataset's registry `default_model`
  // (matched via the same normalize key), else first option. Prevents the
  // pre-existing bug where ProstateX cls users had a model_type from LIDC's
  // 2D option set sitting in state — backend silently substituted SimpleCNN,
  // confusing users. Now we surface only valid choices and pick a sensible
  // default actively.
  useEffect(() => {
    if (!currentModelOptions.length) return;
    const stillValid = currentModelOptions.some((opt) => opt.value === configs.model_type);
    if (stillValid) return;
    const datasetDef = currentDatasetRef ? mockDatasetDefinitions[currentDatasetRef] : null;
    const datasetDefaultName = datasetDef?.default_model;
    let nextValue = currentModelOptions[0].value;
    if (datasetDefaultName) {
      const defKey = normalizeModelName(datasetDefaultName);
      const match = currentModelOptions.find(
        (opt) =>
          normalizeModelName(opt.value) === defKey ||
          normalizeModelName(opt.label) === defKey
      );
      if (match) nextValue = match.value;
    }
    setConfigs((prev) => ({ ...prev, model_type: nextValue }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModelOptions, currentDatasetRef]);

  /* Updated Feature Selection Handlers using intersectedFeatures instead of displayableFeatures */
  const handleSelectNumericalFeatures = () => {
    const numerical = intersectedFeatures.filter(f => {
        const type = inferFeatureType(f);
        return type === 'INT' || type === 'FLOAT';
    });
    setSelectedFeatures(numerical);
    setConfigs(prev => ({ ...prev, selected_features: numerical }));
    debouncedUpdateFeatures(numerical);
  };

  // Filter features based on search
  let activeFeatureList = [];
  if (configs.selected_datasets?.length === 1) {
      activeFeatureList = perDatasetFeaturesMap[configs.selected_datasets[0]] || intersectedFeatures || [];
  } else {
      activeFeatureList = currentFeatureTab === "intersection" 
          ? intersectedFeatures 
          : (perDatasetFeaturesMap[currentFeatureTab] || []);
  }
      
  const filteredFeatures = activeFeatureList.filter(f => f.toLowerCase().includes(featureSearch.toLowerCase()));

  // --- Helper Functions for Filter Logic ---
  const isFeatureFiltered = (feature) => {
    // If no filter entry exists, it's not filtered
    const filter = configs.feature_filters?.[feature];
    if (!filter) return false;

    // If metadata doesn't exist, we can't judge (safest to check vs default)
    const meta = featureMetadata?.[feature];
    if (!meta) return false; 

    if (meta.type === 'numeric') {
        const min = Number(filter.min ?? meta.min);
        const max = Number(filter.max ?? meta.max);
        const metaMin = Number(meta.min);
        const metaMax = Number(meta.max);
        
        // It is filtered if min > meta.min OR max < meta.max
        return min > metaMin || max < metaMax; 
    } else if (meta.type === 'categorical') {
        // Filtered if values are selected AND the set is smaller than all allowed values
        // If values is empty/undefined => interpreted as "All" by our UI logic, so NOT filtered.
        const selectedValues = filter.values;
        if (!selectedValues || selectedValues.length === 0) return false; 
        
        // If selectedValues has same length as allowed_values, effectively not filtered
        if (meta.allowed_values && selectedValues.length === meta.allowed_values.length) return false;

        return true;
    }
    return false;
  };

  const getFilterSummary = (feature) => {
      if (!isFeatureFiltered(feature)) return "";
      const filter = configs.feature_filters?.[feature];
      const meta = featureMetadata?.[feature];
      
      if (!filter || !meta) return "";

      if (meta.type === 'numeric') {
          return `Range: ${filter.min ?? meta.min} - ${filter.max ?? meta.max}`;
      } else if (meta.type === 'categorical') {
          const vals = filter.values || [];
          if (vals.length === 0) return "All values"; // Should not reach here due to isFeatureFiltered check but good safety
          if (vals.length > 3) return `Includes: ${vals.slice(0, 3).join(', ')}...`;
          return `Includes: "${vals.join('", "')}"`;
      }
      return "";
  };

  const generateHardGradient = (colorsArr) => {
      if (!colorsArr || colorsArr.length === 0) return 'transparent';
      if (colorsArr.length === 1) return colorsArr[0];
      const step = 100 / colorsArr.length;
      const stops = colorsArr.map((color, idx) => {
          const start = idx * step;
          const end = (idx + 1) * step;
          return `${color} ${start}%, ${color} ${end}%`;
      });
      return `linear-gradient(to bottom, ${stops.join(', ')})`;
  };

  // Define a palette for up to 10 datasets.
  const datasetColorsPalette = [
      
      '#3b82f6', // blue
     
      '#10b981', // emerald
      '#a855f7', // purple
      '#f59e0b', // amber
      '#0ea5e9', // sky
      '#84cc16', // lime
      '#ec4899', // pink
      'var(--fx-text-secondary)', // slate
      '#14b8a6', // teal
       '#f43f5e' // rose
  ];

  return (
    <Box sx={{ p: 3, pb: 12, maxWidth: 1440, mx: 'auto' }}>
      <HeaderWithControls
        icon={<TuneIcon />}
        title={t('configs.title')}
        subtitle="Configure your federated learning run and data sources."
        customControls={
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<HelpOutlineIcon />}
            onClick={() => setHelpOpen(true)}
            sx={{ color: colors.grey[500], borderColor: colors.grey[800], borderRadius: '8px' }}
          >
            Help
          </Button>
        }
      />
      
      <HelpModal 
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Configuration Help"
        items={[{ title: "Setup", description: "Select mode and parameters." }]}
      />

      {errors.configSave && <Alert severity="error" sx={{ mb: 3 }}>{errors.configSave}</Alert>}

      <Grid container spacing={2.5}>
        {/* MAIN COLUMN: CONTROL BOARD (full width) */}
        <Grid item xs={12}>
            
            {/* 1. Processing Type Selector (Card Row) */}
            <Card sx={{ mb: 2.5, p: 2.5, border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)', bgcolor: 'var(--fx-paper)' }}>
                <StepHeader
                  number={1}
                  title="Processing module"
                  subtitle="Choose the data type and task. This determines which models and datasets are available."
                />
                {/* Prototype: all three tasks are clickable. Active card uses
                    the task's family color (indigo / emerald / teal) so the
                    Processing Module row visually anchors to the same task
                    taxonomy used on /datasets cards. */}
                {(() => {
                  const PROCESSING_CARDS = [
                    {
                      key: "tabular",
                      icon: <TabularIcon size={26} />,
                      title: "Tabular",
                      subtitle: "Structured records · CSV",
                      accent: "var(--fx-cat-indigo)",
                      soft: "var(--fx-cat-indigo-soft)",
                    },
                    {
                      key: "imaging_classification",
                      icon: <ImagingClassificationIcon size={26} />,
                      title: "Imaging · Classification",
                      subtitle: "Patient or patch-level prediction",
                      accent: "var(--fx-cat-emerald)",
                      soft: "var(--fx-cat-emerald-soft)",
                    },
                    {
                      key: "imaging_segmentation",
                      icon: <ImagingSegmentationIcon size={26} />,
                      title: "Imaging · Segmentation",
                      subtitle: "Pixel-level mask prediction",
                      accent: "var(--fx-cat-teal)",
                      soft: "var(--fx-cat-teal-soft)",
                    },
                  ];
                  return (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {PROCESSING_CARDS.map((c) => {
                        const isActive = processingType === c.key;
                        return (
                          <Card
                            key={c.key}
                            onClick={() => handleProcessingTypeChange(c.key)}
                            sx={{
                              flex: 1,
                              cursor: 'pointer',
                              bgcolor: isActive ? 'var(--fx-raised)' : 'var(--fx-inset)',
                              border: isActive
                                ? `1px solid ${c.accent}`
                                : '1px solid var(--fx-border)',
                              // Soft tinted ring instead of a thicker border:
                              // no layout shift on selection, reads in both modes.
                              boxShadow: isActive
                                ? `0 0 0 3px ${c.soft}, var(--fx-shadow-md)`
                                : 'none',
                              position: 'relative',
                              transition: 'all 0.2s',
                              '&:hover': isActive
                                ? {}
                                : { borderColor: 'var(--fx-border-strong)', bgcolor: 'var(--fx-subtle)' },
                            }}
                          >
                            <CardContent
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                '&:last-child': { pb: 2 },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  flexShrink: 0,
                                  bgcolor: isActive ? c.soft : 'var(--fx-border)',
                                  color: isActive ? c.accent : 'var(--fx-text-muted)',
                                  transition: 'all 0.2s',
                                }}
                              >
                                {c.icon}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    color: isActive ? c.accent : 'var(--fx-text-secondary)',
                                  }}
                                >
                                  {c.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    lineHeight: 1.2,
                                    color: isActive ? 'var(--fx-text-secondary)' : 'var(--fx-text-muted)',
                                  }}
                                >
                                  {c.subtitle}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  );
                })()}
            </Card>

            {/* 1b. Site-based dataset selection (mock-backed).
                The SiteSelectionPanel filter follows the active Processing
                Module so the picker only offers capabilities that match
                the chosen task (Tabular / Imaging · Classification /
                Imaging · Segmentation). Selecting nothing keeps the legacy
                tabular flow byte-identical when processing_type === "tabular". */}
            <SiteSelectionPanel taskFilter={processingType} />

            {/* Optional Schema Pre-check (advisory). Does NOT block Confirm
                and Save and does NOT replace the mandatory final validation
                gate on /execute Start. */}
            <SchemaPreCheckPanel processingType={processingType} />

            {/* dev-0.7.21 — Selected Sites Feature Distribution viz.
                Explicit "Compare distributions" CTA (user-confirmed UX choice
                vs auto-fetch). Disabled until at least one completed
                (site_id + capability_id) selection is present and the
                derived dataset_ref is unambiguous. Read-only; no training
                trigger. See FederateX_agent_sync/Backend_To_Frontend/
                2026-06-05_backend_feature_distribution_endpoint_integration_spec.md
                for the full contract this panel implements. */}
            <FeatureDistributionPanel />

            {/* 2. Control Board: Inputs */}
            <Card sx={{ mb: 2.5, p: 2.5, border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)', bgcolor: 'var(--fx-paper)' }}>
                <StepHeader
                  number={4}
                  title="Configuration parameters"
                  subtitle="Pick the model architecture and tune the federated training hyperparameters."
                />

                {/* Group 1: Model Architecture (F1.5 v2 — Dataset Source moved to
                    the Advanced · Simulation Mode accordion at the bottom of this column) */}
                <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                    <Box sx={{ flex: 1, maxWidth: { md: '50%' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <InputLabel sx={{ fontWeight: 600, color: colors.grey[300], fontSize: '0.85rem' }}>MODEL ARCHITECTURE</InputLabel>
                          {/* dev-0.7.9: Anomaly 1 fixed — model_type now
                              propagates from /api/config → execute. Re-enabled
                              with a normal informational tooltip. Backend
                              dispatcher is case-sensitive: option values
                              below are lowercase canonical (`logreg`/`mlp`/
                              `tabnet`/`fttransformer`) which is what backend
                              accepts. */}
                          <Tooltip title="Federated learning model architecture used by all participating sites. Backend honors this selection as of dev-0.7.9. Values are sent lowercase on the wire to match the ClientApp dispatcher.">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Select
                            fullWidth
                            value={currentModelOptions.some((o) => o.value === configs.model_type) ? configs.model_type : ""}
                            onChange={(e) => handleChange("model_type", e.target.value)}
                            sx={{ bgcolor: 'var(--fx-paper)' }}
                            displayEmpty
                        >
                            {currentModelOptions.length === 0 && (
                                <MenuItem value="" disabled>No models registered for this pipeline</MenuItem>
                            )}
                            {currentModelOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                {/* Group 2: Tuning Panel (Hyperparameters) */}
                <Box sx={{
                    bgcolor: 'var(--fx-subtle)',
                    border: '1px solid var(--fx-border)',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid var(--fx-border)',
                        bgcolor: 'var(--fx-paper)',
                    }}>
                         <Box sx={{
                            width: 26, height: 26, borderRadius: '7px',
                            bgcolor: 'rgba(0,135,180,0.10)', color: '#0087B4',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                         }}>
                            <TuneIcon sx={{ fontSize: 16 }} />
                         </Box>
                         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.grey[600], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Hyperparameter Configurations
                         </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <InputLabel sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.grey[500], letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                    Global Rounds
                                </InputLabel>
                                <Tooltip title="Number of overall federated learning rounds initiated by the server.">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                type="number"
                                value={configs["num-server-rounds"]}
                                onChange={(e) => handleChange("num-server-rounds", e.target.value)}
                                InputProps={{ 
                                    inputProps: { min: 1 },
                                    endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: colors.grey[400] }}>rounds</Typography></InputAdornment>
                                }}
                            />
                            <FormHelperText sx={{ fontSize: '0.7rem', color: colors.grey[500], mt: 0.5, mx: 1.5 }}>
                                Rec: 1 - 10
                            </FormHelperText>
                        </Grid>
                        <Grid item xs={6}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <InputLabel sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.grey[500], letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                    Local Epochs
                                </InputLabel>
                                <Tooltip title="Number of training passes each client performs on their local data per round.">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                type="number"
                                value={configs["local-epochs"]}
                                onChange={(e) => handleChange("local-epochs", e.target.value)}
                                InputProps={{ 
                                    inputProps: { min: 1 },
                                    endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: colors.grey[400] }}>epochs</Typography></InputAdornment>
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--fx-paper)' } }}
                            />
                            
                        </Grid>
                        <Grid item xs={6}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <InputLabel sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.grey[500], letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                    Batch Size
                                </InputLabel>
                                <Tooltip title="Number of samples processed before the model is updated.">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                type="number"
                                value={configs["batch-size"]}
                                onChange={(e) => handleChange("batch-size", e.target.value)}
                                InputProps={{ 
                                    inputProps: { min: 1 },
                                    endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: colors.grey[400] }}>items</Typography></InputAdornment>
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--fx-paper)' } }}
                            />
                            <FormHelperText sx={{ fontSize: '0.7rem', color: colors.grey[500], mt: 0.5, mx: 1.5 }}>
                                Powers of 2 (32, 64, 128)
                            </FormHelperText>
                        </Grid>
                        <Grid item xs={6}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <InputLabel sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.grey[500], letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                    Learning Rate
                                </InputLabel>
                                {/* dev-0.7.9: Anomaly 1 fixed — learning-rate
                                    now propagates from /api/config → execute. */}
                                <Tooltip title="Step size at each iteration while moving toward a minimum of a loss function. Backend honors this value as of dev-0.7.9.">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                type="number"
                                value={configs["learning-rate"]}
                                onChange={(e) => handleChange("learning-rate", e.target.value)}
                                InputProps={{
                                    inputProps: { step: 0.001 },
                                    endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ color: colors.grey[400] }}>rate</Typography></InputAdornment>
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--fx-paper)' } }}
                            />
                            <FormHelperText sx={{ fontSize: '0.7rem', color: colors.grey[500], mt: 0.5, mx: 1.5 }}>
                                Rec: 0.001 - 0.01
                            </FormHelperText>
                        </Grid>
                    </Grid>
                    </Box>
                </Box>


                {/* Feature Selection (tabular) — LEGACY simulation/demo flow only.
                    New flow: feature/schema alignment is handled at dataset &
                    capability registration (site-local schema validation), so
                    users no longer pick features here. If there's a mismatch,
                    the fix is to re-register / refresh the dataset capability —
                    not to prune columns at training time. This block is therefore
                    gated to the "Advanced · Simulation Mode (legacy)" path
                    (`simulationExpanded`) and stays hidden in the main
                    site-local flow. All selectedFeatures wiring is left intact so
                    the legacy/demo path is unchanged. */}
                {simulationExpanded && processingType === 'tabular' && displayableFeaturesLoading ? (
                     <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', py: 8 }}>
                         <CircularProgress size={40} thickness={4} sx={{ color: '#06b6d4' }} />
                     </Box>
                ) : simulationExpanded && processingType === 'tabular' && activeFeatureList.length > 0 && (
                     <Box sx={{ mt: 4 }}>
                        {/* TABS NAVIGATION */}
                        {configs.selected_datasets?.length > 1 && (
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs 
                                    value={currentFeatureTab} 
                                    onChange={(e, newValue) => setCurrentFeatureTab(newValue)} 
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
                                >
                                    <Tab label="All Features" value="intersection" />
                                    {configs.selected_datasets.map((path, idx) => (
                                        <Tab 
                                            key={path} 
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: datasetColorsPalette[idx % datasetColorsPalette.length] }} />
                                                    <Typography sx={{ fontWeight: 600, textTransform: 'none', fontSize: '0.875rem' }}>{path.split('/').pop()}</Typography>
                                                </Box>
                                            } 
                                            value={path} 
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                                    Feature Selection
                                </Typography>
                                <Chip 
                                    label={`${selectedFeatures.length} / ${activeFeatureList.length}`} 
                                    size="small"  
                                    sx={{ 
                                        height: 24, 
                                        bgcolor: selectedFeatures.length > 0 ? 'rgba(6, 182, 212, 0.2)' : colors.grey[800], 
                                        color: selectedFeatures.length > 0 ? '#06b6d4' : colors.grey[400], 
                                        fontWeight: 700,
                                        fontFamily: 'var(--fx-font-mono)',
                                        fontVariantNumeric: 'tabular-nums'
                                    }}
                                />
                            </Box>
                            
                            {/* Toolbar Presets */}
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Select all numerical features (INT, FLOAT)">
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={handleSelectNumericalFeatures}
                                        startIcon={<Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'var(--fx-info-soft)', color: 'var(--fx-info-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 'bold' }}>#</Box>}
                                        sx={{ 
                                            textTransform: 'none', 
                                            color: colors.grey[400], 
                                            borderColor: colors.grey[700], 
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            '&:hover': { borderColor: 'var(--fx-paper)', bgcolor: 'rgba(255,255,255,0.1)' } 
                                        }}
                                    >
                                        All Numerical
                                    </Button>
                                </Tooltip>
                                
                                <Tooltip title="Select all categorical features (Object, Cat)">
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={() => {
                                            const categorical = intersectedFeatures.filter(f => inferFeatureType(f) === 'CAT');
                                            setSelectedFeatures(categorical);
                                            setConfigs(prev => ({ ...prev, selected_features: categorical }));
                                            debouncedUpdateFeatures(categorical);
                                        }}
                                        startIcon={<Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: 'var(--fx-warning-soft)', color: 'var(--fx-warning-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 'bold' }}>Aa</Box>}
                                        sx={{ 
                                            textTransform: 'none', 
                                            color: colors.grey[400], 
                                            borderColor: colors.grey[700], 
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            '&:hover': { borderColor: 'var(--fx-paper)',  bgcolor: 'rgba(255,255,255,0.1)' } 
                                        }}
                                    >
                                        All Categorical
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Select All Features">
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={() => {
                                            const all = intersectedFeatures;
                                            // Toggle: If all selected, clear. Else select all.
                                            if (selectedFeatures.length === all.length) {
                                                setSelectedFeatures([]);
                                                setConfigs(prev => ({ ...prev, selected_features: [] }));
                                                debouncedUpdateFeatures([]);
                                            } else {
                                                setSelectedFeatures(all);
                                                setConfigs(prev => ({ ...prev, selected_features: all }));
                                                debouncedUpdateFeatures(all);
                                            }
                                        }}
                                        startIcon={<CheckBoxIcon sx={{ fontSize: 14 }} />}
                                        sx={{ 
                                            textTransform: 'none', 
                                            color: colors.grey[400], 
                                            borderColor: colors.grey[700], 
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            '&:hover': { borderColor: 'var(--fx-paper)',  bgcolor: 'rgba(255,255,255,0.1)' } 
                                        }}
                                    >
                                        Select All
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </Box>
                        
                        {/* Search Bar */}
                        <TextField 
                            fullWidth
                            placeholder="Type to filter features..."
                            value={featureSearch}
                            onChange={(e) => setFeatureSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.grey[500], fontSize: 18 }} /></InputAdornment>,
                                endAdornment: featureSearch && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setFeatureSearch("")}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': { bgcolor: 'var(--fx-paper)', borderRadius: '10px' } 
                            }}
                        />

                        {/* --- THE SMART CHIP GRID --- */}
                        {(() => {
                            const renderFeatureCard = (feature) => {
                                const isSelected = selectedFeatures.includes(feature);
                                const isIntersected = intersectedFeatures.includes(feature);
                                const isSelectable = true;
                                const type = inferFeatureType(feature);
                                const isNumerical = type === 'INT' || type === 'FLOAT';

                                const featureDatasets = configs.selected_datasets.filter(dsPath => perDatasetFeaturesMap[dsPath]?.includes(feature));
                                const numDatasets = featureDatasets.length;
                                
                                const featureColors = featureDatasets.map(dsPath => {
                                    const idx = configs.selected_datasets.indexOf(dsPath);
                                    return datasetColorsPalette[idx % datasetColorsPalette.length];
                                });

                                let stripeBg = 'transparent';
                                if (numDatasets === 1) {
                                    stripeBg = featureColors[0];
                                } else if (numDatasets > 1) {
                                    stripeBg = generateHardGradient(featureColors);
                                }

                                const tooltipText = featureDatasets.map(d => d.split('/').pop()).join(', ');

                                let badgeText = '';
                                if (numDatasets === 1) {
                                    const filename = featureDatasets[0].split('/').pop().replace('.csv', '');
                                    const parts = filename.split('_').filter(p => p.toLowerCase() !== 'dataset');
                                    if (parts.length > 0) {
                                        badgeText = parts.map(p => p[0].toUpperCase()).join('').substring(0, 3);
                                    } else {
                                        badgeText = filename.substring(0, 2).toUpperCase();
                                    }
                                } else if (numDatasets > 1) {
                                    badgeText = `+${numDatasets}`;
                                }

                                return (
                                    <Box
                                        key={feature}
                                        onClick={() => {
                                            if (isSelectable) {
                                                handleFeatureSelectionChange(feature, !isSelected);
                                            }
                                        }}
                                        sx={{
                                            cursor: isSelectable ? 'pointer' : 'not-allowed',
                                            userSelect: 'none',
                                            borderRadius: '10px',
                                            height: '60px',
                                            border: isSelected ? '1px solid #06b6d4' : '1px solid var(--fx-border)',
                                            bgcolor: isSelected ? 'var(--fx-info-soft)' : (isSelectable ? 'var(--fx-paper)' : 'var(--fx-subtle)'),
                                            opacity: isSelectable ? 1 : 0.6,
                                            pl: 3, // Room for the stripe
                                            pr: 2,
                                            py: 1,
                                            // Flex basis logic for grid
                                            width: { xs: 'calc(50% - 6px)', sm: 'calc(33% - 8px)', md: 'calc(25% - 9px)' }, 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                transform: isSelectable ? 'translateY(-2px)' : 'none',
                                                boxShadow: isSelected ? '0 4px 12px rgba(6, 182, 212, 0.2)' : (isSelectable ? '0 4px 12px rgba(0,0,0,0.05)' : 'none'),
                                                borderColor: isSelected ? '#06b6d4' : (isSelectable ? 'var(--fx-text-muted)' : 'var(--fx-border)')
                                            }
                                        }}
                                    >
                                        {/* Colored Left Stripe */}
                                        <Box sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '6px',
                                            background: stripeBg
                                        }} />
                                        
                                        {/* Row 1: Feature Name */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                            <Tooltip title={feature} placement="top" disableInteractive enterDelay={500}>
                                                <Typography 
                                                    variant="body2" 
                                                    noWrap 
                                                    sx={{ 
                                                        fontWeight: isSelected ? 800 : 600, 
                                                        color: isSelected ? 'var(--fx-text-strong)' : 'var(--fx-text-secondary)',
                                                        fontSize: '0.85rem',
                                                        flex: 1
                                                    }}
                                                >
                                                    {feature}
                                                </Typography>
                                            </Tooltip>
                                        </Box>

                                        {/* Row 2: Type Badge & Status */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                 {/* Visual Type Icon */}
                                                {isNumerical ? (
                                                     <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'var(--fx-info-soft)', color: 'var(--fx-info-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                                                        <TagIcon sx={{ fontSize: 10 }} />
                                                     </Box>
                                                ) : (
                                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: 'var(--fx-warning-soft)', color: 'var(--fx-warning-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                                                        <AbcIcon sx={{ fontSize: 12 }} />
                                                    </Box>
                                                )}
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isSelected ? 'var(--fx-info-text)' : 'var(--fx-text-muted)', fontWeight: 600 }}>
                                                    {type}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                

                                                {/* Filter Tools (Only if Selected) */}
                                                {isSelected && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {/* Filter Badge if active filters exist */}
                                                        {isFeatureFiltered(feature) && (
                                                            <Box sx={{ 
                                                                width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', 
                                                                boxShadow: '0 0 5px #f59e0b', mr: 0.5 
                                                            }} />
                                                        )}
                                                   
                                                     <Tooltip title="Apply Filter">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => handleOpenDrawer(feature, e)}
                                                        sx={{ 
                                                            p: 0.5, 
                                                            color: '#06b6d4',
                                                            bgcolor: 'rgba(6, 182, 212, 0.1)',
                                                            '&:hover': { color: '#0891b2', bgcolor: 'rgba(6, 182, 212, 0.2)' }
                                                        }}
                                                    >
                                                        <TuneIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                            {/* Source Indicator Badge */}
                                            {numDatasets > 0 && (
                                                <Tooltip 
                                                    title={
                                                        <Stack spacing={0.5} sx={{ p: 0.5 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'var(--fx-subtle)' }}>
                                                                Found in {numDatasets} dataset{numDatasets > 1 ? 's' : ''}
                                                            </Typography>
                                                            {featureDatasets.map(dsPath => {
                                                                const idx = configs.selected_datasets.indexOf(dsPath);
                                                                const dsColor = datasetColorsPalette[idx % datasetColorsPalette.length];
                                                                return (
                                                                    <Box key={dsPath} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dsColor, flexShrink: 0 }} />
                                                                        <Typography variant="caption" sx={{ color: 'var(--fx-border-strong)' }}>{dsPath.split('/').pop()}</Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Stack>
                                                    }
                                                    placement="top" 
                                                    disableInteractive
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: '#1e293b',
                                                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                                                border: '1px solid var(--fx-text-secondary)',
                                                                borderRadius: '8px',
                                                                p: 1
                                                            }
                                                        },
                                                        arrow: {
                                                            sx: { color: '#1e293b' }
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ 
                                                        px: 0.8, py: 0.2, 
                                                        borderRadius: '4px', 
                                                        fontSize: '0.6rem', 
                                                        fontWeight: 700, 
                                                        bgcolor: numDatasets === 1 ? featureColors[0] + '20' : 'var(--fx-border)', 
                                                        color: numDatasets === 1 ? featureColors[0] : 'var(--fx-text-secondary)',
                                                        border: `1px solid ${numDatasets === 1 ? featureColors[0] + '40' : 'var(--fx-border-strong)'}`,
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help'
                                                    }}>
                                                        {badgeText}
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            };

                            if (filteredFeatures.length === 0) {
                                return (
                                    <Box sx={{ width: '100%', py: 4, textAlign: 'center', border: '1px dashed var(--fx-text-secondary)', borderRadius: 2 }}>
                                        <Typography color="textSecondary">No features match your search.</Typography>
                                    </Box>
                                );
                            }

                            if (configs.selected_datasets?.length <= 1 || currentFeatureTab !== "intersection") {
                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        {filteredFeatures.map(renderFeatureCard)}
                                    </Box>
                                );
                            }

                            // Grouping logic for multiple datasets
                            const totalDatasets = configs.selected_datasets.length;
                            const allDatasetsFeatures = filteredFeatures.filter(f => {
                                const c = configs.selected_datasets.filter(ds => perDatasetFeaturesMap[ds]?.includes(f)).length;
                                return c === totalDatasets;
                            });
                            const multiDatasetsFeatures = filteredFeatures.filter(f => {
                                const c = configs.selected_datasets.filter(ds => perDatasetFeaturesMap[ds]?.includes(f)).length;
                                return c >= 2 && c < totalDatasets;
                            });
                            const singleDatasetFeatures = filteredFeatures.filter(f => {
                                const c = configs.selected_datasets.filter(ds => perDatasetFeaturesMap[ds]?.includes(f)).length;
                                return c === 1;
                            });

                            return (
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {allDatasetsFeatures.length > 0 && (
                                        <Box>
                                            <Typography sx={{ color: colors.grey[400], fontWeight: 600, mb: 1.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Visible in All Datasets
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                {allDatasetsFeatures.map(renderFeatureCard)}
                                            </Box>
                                        </Box>
                                    )}
                                    
                                    {multiDatasetsFeatures.length > 0 && (
                                        <Box>
                                            <Typography sx={{ color: colors.grey[400], fontWeight: 600, mb: 1.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Visible in Multiple Datasets
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                {multiDatasetsFeatures.map(renderFeatureCard)}
                                            </Box>
                                        </Box>
                                    )}

                                    {singleDatasetFeatures.length > 0 && (
                                        <Box>
                                            <Typography sx={{ color: colors.grey[400], fontWeight: 600, mb: 1.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Visible in Single Dataset
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                {singleDatasetFeatures.map(renderFeatureCard)}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })()}
                     </Box>
                )}


            </Card>

            {/* F1.5 v2 — Advanced · Simulation Mode accordion. Hidden from the
                page per product decision; the legacy Dataset Source flow stays
                in the code (wrapped in `false`) but never renders. */}
            {false && (
            <SimulationModeAccordion
                expanded={simulationExpanded}
                onChange={(isOpen) => setSimulationExpanded(isOpen)}
            >
                <InputLabel sx={{ mb: 1, fontWeight: 600, color: colors.grey[300], fontSize: '0.85rem' }}>DATASET SOURCE (legacy)</InputLabel>

                {/* Custom Smart Trigger Button */}
                <Button
                    fullWidth
                    onClick={() => setDatasetModalOpen(true)}
                    sx={{
                        bgcolor: 'var(--fx-paper)',
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: '10px',
                        color: configs.selected_datasets?.length > 0 ? colors.grey[100] : colors.grey[400],
                        justifyContent: 'space-between',
                        px: 2,
                        py: '14px',
                        textTransform: 'none',
                        '&:hover': {
                            border: '1px solid #000000',
                            bgcolor: 'var(--fx-paper)'
                        }
                    }}
                    endIcon={<StorageIcon color="action" />}
                >
                     {configs.selected_datasets?.length > 0 ? (
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             {configs.selected_datasets.length === 1 ? (
                                 <Typography>{configs.selected_datasets[0].split('/').pop()}</Typography>
                             ) : (
                                 <Tooltip title={
                                     <Stack>
                                         {configs.selected_datasets.map(d => (
                                             <Typography key={d} variant="caption" sx={{ display: 'block' }}>
                                                 • {d.split('/').pop()}
                                             </Typography>
                                         ))}
                                     </Stack>
                                 }>
                                     <Typography fontWeight="700" color="#0fa5e8">
                                         {configs.selected_datasets.length} Datasets Selected
                                     </Typography>
                                 </Tooltip>
                             )}
                         </Box>
                     ) : (
                         <Typography color={colors.grey[500]}>Select Datasets...</Typography>
                     )}
                </Button>

                <DatasetSelectionModal
                    isOpen={datasetModalOpen}
                    onClose={() => setDatasetModalOpen(false)}
                    onConfirm={handleDatasetsConfirmed}
                    processingModule={processingType}
                    availableDatasets={availableDatasets}
                    currentSelection={configs.selected_datasets || []}
                />

                {/* Dataset Metadata Display */}
                <Box sx={{ mt: 1.5 }}>
                    {(() => {
                        // Simplified Metadata for Multi-Select
                        if (!configs.selected_datasets || configs.selected_datasets.length === 0) return null;

                        const primaryPath = configs.selected_datasets[0];
                        const selectedDatasetInfo = availableDatasets.find(d =>
                            (typeof d === 'string' ? d : (d.path || d.name)) === primaryPath
                        );

                        // Only show a skeleton while the dataset CATALOG is genuinely
                        // still loading. Previously this treated "no metadata found"
                        // (e.g. a site-capability id that isn't in the catalog) as a
                        // perpetual loading state, leaving a grey loader on screen
                        // forever. Now: catalog empty → skeleton; catalog loaded but no
                        // metadata for this entry → fall through to "Unknown" display.
                        const catalogLoading = !Array.isArray(availableDatasets) || availableDatasets.length === 0;
                        const size = selectedDatasetInfo?.size || 'Unknown';
                        const samples = selectedDatasetInfo?.num_sample || selectedDatasetInfo?.num_samples || selectedDatasetInfo?.samples || 'Unknown';

                        if (catalogLoading && primaryPath) {
                            return (
                                <Skeleton variant="rectangular" height={48} animation="wave" sx={{ borderRadius: 1, bgcolor: 'var(--fx-subtle)' }} />
                            );
                        }

                        return (
                            <Box sx={{ display: 'flex', gap: 2, bgcolor: 'var(--fx-subtle)', alignItems: 'center' }}>
                                {configs.selected_datasets.length > 1 ? (
                                     <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)' }}>
                                         Metadata shown for primary dataset only
                                     </Typography>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex',  alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, display: 'block',  textTransform: 'uppercase' }}>
                                                SAMPLES
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                                                {typeof samples === 'number' ? samples.toLocaleString() : samples}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: '1px', height: 12, bgcolor: 'var(--fx-border-strong)' }} />
                                        <Box sx={{ display: 'flex',  alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, display: 'block',  textTransform: 'uppercase' }}>
                                                SIZE
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                                                {size}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        );
                    })()}
                </Box>
            </SimulationModeAccordion>
            )}
        </Grid>

        {/* Feature Inspector Drawer */}
        <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleCloseDrawer}
            sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 400 }, p: 3, bgcolor: 'var(--fx-subtle)' } }}
        >
            {currentInspectingFeature && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--fx-text-strong)' }}>
                                {currentInspectingFeature}
                            </Typography>
                             <Chip 
                                label={inferFeatureType(currentInspectingFeature)}
                                size="small" 
                                sx={{ mt: 0.5, height: 20, bgcolor: 'var(--fx-border)', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.7rem' }} 
                            />
                        </Box>
                        <IconButton onClick={handleCloseDrawer} sx={{ color: 'var(--fx-text-secondary)' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Box>
                         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--fx-text-secondary)', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Value Constraints
                        </Typography>

                        {featureMetadata && featureMetadata[currentInspectingFeature] ? (
                            <>
                                {/* Numeric Controls */}
                                {featureMetadata[currentInspectingFeature].type === 'numeric' && (
                                    <Box sx={{ px: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', mb: 4, display: 'block' }}>
                                            Define the allowed range for this feature.
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Min: {featureMetadata[currentInspectingFeature].min}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Max: {featureMetadata[currentInspectingFeature].max}</Typography>
                                        </Box>

                                        <Slider
                                            value={[
                                                Number(configs.feature_filters?.[currentInspectingFeature]?.min ?? featureMetadata[currentInspectingFeature].min),
                                                Number(configs.feature_filters?.[currentInspectingFeature]?.max ?? featureMetadata[currentInspectingFeature].max)
                                            ]}
                                            onChange={(_, newValue) => {
                                                handleFilterChange(currentInspectingFeature, 'min', newValue[0]);
                                                handleFilterChange(currentInspectingFeature, 'max', newValue[1]);
                                            }}
                                            min={featureMetadata[currentInspectingFeature].min}
                                            max={featureMetadata[currentInspectingFeature].max}
                                            valueLabelDisplay="auto"
                                            sx={{ color: '#06b6d4', mb: 4 }}
                                        />

                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                                label="Min Value"
                                                size="small"
                                                type="number"
                                                fullWidth
                                                value={configs.feature_filters?.[currentInspectingFeature]?.min ?? ''}
                                                onChange={(e) => handleFilterChange(currentInspectingFeature, 'min', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ 
                                                    min: featureMetadata[currentInspectingFeature].min,
                                                    max: featureMetadata[currentInspectingFeature].max
                                                }}
                                                sx={{ bgcolor: 'var(--fx-paper)' }}
                                            />
                                            <TextField
                                                label="Max Value"
                                                size="small"
                                                type="number"
                                                fullWidth
                                                value={configs.feature_filters?.[currentInspectingFeature]?.max ?? ''}
                                                onChange={(e) => handleFilterChange(currentInspectingFeature, 'max', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ 
                                                    min: featureMetadata[currentInspectingFeature].min,
                                                    max: featureMetadata[currentInspectingFeature].max
                                                }}
                                                sx={{ bgcolor: 'var(--fx-paper)' }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                
                                {/* Categorical Controls */}
                                {featureMetadata[currentInspectingFeature].type === 'categorical' && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', mb: 2, display: 'block' }}>
                                            Select allowed values (leave empty to allow all).
                                        </Typography>
                                        
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Select Values</InputLabel>
                                            <Select
                                                multiple
                                                value={configs.feature_filters?.[currentInspectingFeature]?.values || []}
                                                onChange={(e) => handleFilterChange(currentInspectingFeature, 'values', e.target.value)}
                                                label="Select Values"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{ bgcolor: 'var(--fx-paper)' }}
                                                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                                            >
                                                {(featureMetadata[currentInspectingFeature].allowed_values || []).map((val) => (
                                                    <MenuItem key={val} value={val}>
                                                        <Checkbox checked={(configs.feature_filters?.[currentInspectingFeature]?.values || []).indexOf(val) > -1} />
                                                        <ListItemText primary={val} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Quick Actions */}
                                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                            <Button 
                                                size="small" 
                                                variant="outlined"
                                                onClick={() => handleFilterChange(currentInspectingFeature, 'values', featureMetadata[currentInspectingFeature].allowed_values)}
                                            >
                                                Select All
                                            </Button>
                                            <Button 
                                                size="small" 
                                                variant="outlined" 
                                                color="error"
                                                onClick={() => handleFilterChange(currentInspectingFeature, 'values', [])}
                                            >
                                                Clear
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'var(--fx-paper)', borderRadius: 2, border: '1px dashed var(--fx-border-strong)' }}>
                                <Typography color="text.secondary">
                                    No metadata available for this feature.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 4 }}>
                        <Button 
                            fullWidth 
                            variant="contained" 
                            onClick={handleCloseDrawer}
                            sx={{ bgcolor: '#0f172a', py: 1.5 }}
                        >
                            Done
                        </Button>
                    </Box>
                </>
            )}
        </Drawer>


        {/* BOTTOM: Run-training confirmation card (full width) */}
        <Grid item xs={12}>
            <Card sx={{
                border: '1px solid var(--fx-border)',
                borderRadius: 2,
                boxShadow: 'var(--fx-shadow-sm)',
                bgcolor: 'var(--fx-paper)',
                overflow: 'hidden',
            }}>
                {/* Accent top bar */}
                <Box sx={{ height: 3, bgcolor: '#10b981' }} />
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {saveBlockedMessage && (
                        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSaveBlockedMessage(null)}>
                            {saveBlockedMessage}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap' }}>
                        {/* Identity */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                            <Box sx={{
                                width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                                bgcolor: 'rgba(16,185,129,0.10)', color: '#059669',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <PlayArrowIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, color: 'var(--fx-text-primary)', fontSize: '1rem', lineHeight: 1.25 }}>
                                    Ready to run training
                                </Typography>
                                <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.8125rem', mt: 0.25 }}>
                                    Review your configuration, then confirm to launch the federated session.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Key facts */}
                        <Stack
                            direction="row"
                            spacing={2.5}
                            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
                            divider={<Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'var(--fx-border)' }} />}
                        >
                            {(() => {
                              // Pipeline-aware per-round estimate (calibrated 2026-06-10
                              // against typical dev-cluster wall-clock):
                              //   tabular              ≈ 30s / round
                              //   imaging_classification ≈ 45s / round
                              //   imaging_segmentation   ≈ 180s / round (3 min)
                              const PER_ROUND_MIN = {
                                tabular: 0.5,
                                imaging_classification: 0.75,
                                imaging_segmentation: 3,
                              };
                              const rounds = Number(configs['num-server-rounds']) || 0;
                              const perRound = PER_ROUND_MIN[processingType] ?? 0.5;
                              const estMin = rounds * perRound;
                              const estLabel = estMin >= 60
                                ? `~${(estMin / 60).toFixed(1)} h`
                                : `~${estMin.toFixed(1)} min`;
                              return [
                                ['Mode', processingType.toUpperCase()],
                                ['Model', currentModelOptions.find(m => m.value === configs.model_type)?.label || configs.model_type],
                                ['Rounds', configs['num-server-rounds']],
                                ['Epochs', configs['local-epochs']],
                                ['Est. duration', estLabel],
                              ];
                            })().map(([label, value]) => (
                                <Box key={label} sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        {label}
                                    </Typography>
                                    <Typography sx={{ color: 'var(--fx-text-strong)', fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }}>
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>

                        {/* Confirm */}
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={configSaving}
                            endIcon={configSaving ? <CircularProgress size={20} color="inherit" /> : <KeyboardDoubleArrowRightIcon />}
                            sx={{
                                bgcolor: '#10b981',
                                color: 'white',
                                fontWeight: 700,
                                py: 1.25,
                                px: 3,
                                fontSize: '0.9375rem',
                                textTransform: 'none',
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 6px -1px rgba(16,185,129,0.4)',
                                '&:hover': { bgcolor: '#059669' },
                            }}
                        >
                            {configSaving ? 'Initializing...' : 'Confirm & Save'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Configs;
